import { CancelOptions, QueryClient, QueryFilters } from "@tanstack/react-query";
import { ParamsForMethod, QueryDataForMethod, QueryMethodForModel, ModelName, TellescopeQueryKey } from "../../types";
import { TellescopeQueryFilters } from "../../hooks";
import { buildTellescopeQueryKey } from "../../keys";

/**
 * Arguments for canceling queries.
 *
 * @template Model - The Tellescope model name
 * @template Method - The query method for the model
 * @template TError - The error type for the queries
 */
export interface CancelTellescopeQueriesArgs<
  Model extends ModelName,
  Method extends QueryMethodForModel<Model>,
  TError = Error,
> {
  /** The Tellescope model name */
  model: Model;
  /** Query method name */
  method: Method;
  /** Optional request parameters to narrow down queries. If omitted, cancels all queries for the method. */
  request?: Partial<ParamsForMethod<Model, Method>>;
  /** The TanStack Query client instance */
  queryClient: QueryClient;
  /** Optional query filters to narrow down which queries to cancel */
  filters?: Omit<
    TellescopeQueryFilters<
      QueryDataForMethod<Model, Method>,
      TError,
      QueryDataForMethod<Model, Method>,
      TellescopeQueryKey<Model, Method>
    >,
    "queryKey"
  >;
  /** Optional cancel options */
  options?: CancelOptions;
}

/**
 * Cancels outgoing queries for matching Tellescope queries.
 * This is useful for canceling requests when a component unmounts or when you want to prevent unnecessary network requests.
 *
 * @param args - The cancel queries arguments
 * @returns A promise that resolves when cancellation is complete
 *
 * @example
 * ```tsx
 * // Cancel all queries for a method
 * await cancelTellescopeQueries({
 *   queryClient,
 *   model: "users",
 *   method: "getOne",
 * });
 *
 * // Cancel specific query
 * await cancelTellescopeQueries({
 *   queryClient,
 *   model: "users",
 *   method: "getOne",
 *   request: { id: "user-123" },
 * });
 *
 * // Cancel with filters
 * await cancelTellescopeQueries({
 *   queryClient,
 *   model: "users",
 *   method: "getSome",
 *   filters: {
 *     type: "active", // Only cancel active queries
 *   },
 * });
 *
 * // Cancel fetching queries
 * await cancelTellescopeQueries({
 *   queryClient,
 *   model: "users",
 *   method: "getOne",
 *   filters: {
 *     fetchStatus: "fetching", // Only cancel queries that are currently fetching
 *   },
 * });
 * ```
 */
export const cancelTellescopeQueries = async <
  Model extends ModelName,
  Method extends QueryMethodForModel<Model>,
  TError = Error,
>({
  model,
  method,
  request,
  queryClient,
  filters,
  options,
}: CancelTellescopeQueriesArgs<Model, Method, TError>) => {
  const queryKey = buildTellescopeQueryKey({ model, method, request });

  return queryClient.cancelQueries(
    {
      queryKey,
      ...filters,
    } as QueryFilters<TellescopeQueryKey<Model, Method>>,
    options
  );
};
